import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import BatchStatusBadge from './BatchStatusBadge';
import ProcessingProgressBar from './ProcessingProgressBar';
import { supabase } from '../../../lib/supabase';

const TABS = [
  { id: 'details', label: 'Detalles' },
  { id: 'history', label: 'Historial' },
];

// ─── Timeline helpers ─────────────────────────────────────────────────────────

const EVENT_TYPES = {
  CREATED: { icon: 'PlusCircle', color: 'text-blue-600', bg: 'bg-blue-100', dot: 'bg-blue-500', label: 'Lote creado' },
  STATUS_CHANGE: { icon: 'RefreshCw', color: 'text-amber-600', bg: 'bg-amber-100', dot: 'bg-amber-500', label: 'Cambio de estado' },
  EDIT: { icon: 'Pencil', color: 'text-purple-600', bg: 'bg-purple-100', dot: 'bg-purple-500', label: 'Edición guardada' },
  SOLD: { icon: 'ShoppingCart', color: 'text-emerald-600', bg: 'bg-emerald-100', dot: 'bg-emerald-500', label: 'Venta registrada' },
  RISK_OVERRIDE: { icon: 'ShieldAlert', color: 'text-orange-600', bg: 'bg-orange-100', dot: 'bg-orange-500', label: 'Riesgo asumido' },
};

function fmtDateTime(iso) {
  if (!iso) return { date: '—', time: '' };
  const d = new Date(iso);
  return {
    date: d?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    time: d?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
  };
}

function buildTimelineFromBatch(batch, auditEvents, sale) {
  const events = [];

  // Creation event
  if (batch?.createdAt) {
    events?.push({
      type: 'CREATED',
      ts: batch?.createdAt,
      description: `Lote ${batch?.batchId} creado — ${batch?.metal}${batch?.category ? ` / ${batch?.category}` : ''}`,
      actor: null,
    });
  }

  // Audit log events
  (auditEvents || [])?.forEach(ev => {
    const oldV = ev?.old_values || {};
    const newV = ev?.new_values || {};

    if (ev?.action === 'RISK_OVERRIDE') {
      events?.push({
        type: 'RISK_OVERRIDE',
        ts: ev?.created_at,
        description: `Precio negociado con riesgo asumido`,
        actor: ev?.user_email,
      });
      return;
    }

    if (oldV?.batch_status && newV?.batch_status && oldV?.batch_status !== newV?.batch_status) {
      events?.push({
        type: 'STATUS_CHANGE',
        ts: ev?.created_at,
        description: `Estado cambiado: "${oldV?.batch_status}" → "${newV?.batch_status}"`,
        actor: ev?.user_email,
      });
    } else if (ev?.action === 'UPDATE') {
      // Generic edit
      const changedFields = Object.keys(newV)?.filter(k => newV?.[k] !== oldV?.[k] && k !== 'updated_at');
      if (changedFields?.length > 0) {
        const fieldLabels = {
          yield_est: 'Yield estimado',
          yield_actual: 'Yield real',
          logistics_cost: 'Coste logística',
          progress: 'Progreso',
          notes: 'Notas',
          max_buy_price: 'Precio máx.',
          expected_margin: 'Margen esperado',
        };
        const readable = changedFields?.map(f => fieldLabels?.[f] || f)?.join(', ');
        events?.push({
          type: 'EDIT',
          ts: ev?.created_at,
          description: `Edición guardada — campos: ${readable}`,
          actor: ev?.user_email,
        });
      }
    }
  });

  // Sale event
  if (sale) {
    events?.push({
      type: 'SOLD',
      ts: sale?.sold_at || sale?.created_at,
      description: `Venta registrada a ${sale?.sale_price_eur_per_ton || sale?.sale_price_per_ton ? `${parseFloat(sale?.sale_price_eur_per_ton || sale?.sale_price_per_ton)?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t` : '—'}${sale?.buyer_name ? ` — ${sale?.buyer_name}` : ''}`,
      actor: null,
    });
  }

  // Sort newest first
  events?.sort((a, b) => new Date(b.ts) - new Date(a.ts));
  return events;
}

function TimelineItem({ event, isLast }) {
  const cfg = EVENT_TYPES?.[event?.type] || EVENT_TYPES?.EDIT;
  const { date, time } = fmtDateTime(event?.ts);
  return (
    <div className="flex gap-3">
      {/* Dot + line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${cfg?.bg} flex-shrink-0`}>
          <Icon name={cfg?.icon} size={13} color="currentColor" className={cfg?.color} strokeWidth={2} />
        </div>
        {!isLast && <div className="w-px flex-1 bg-slate-200 mt-1 mb-1" />}
      </div>
      {/* Content */}
      <div className={`pb-4 flex-1 min-w-0 ${isLast ? '' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-slate-700 leading-snug" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            {event?.description}
          </p>
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-xs text-slate-400 font-mono">{date} {time}</span>
          {event?.actor && (
            <span className="text-xs text-slate-400" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>· {event?.actor}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BatchDetailModal({ batch, onClose, onEdit, onStatusChange }) {
  const [activeTab, setActiveTab] = useState('details');
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (activeTab !== 'history' || !batch?.id) return;
    const load = async () => {
      setLoadingHistory(true);
      try {
        const [auditRes, saleRes] = await Promise.all([
          supabase?.from('audit_log')?.select('id, action, entity_type, old_values, new_values, user_email, created_at')?.eq('entity_type', 'batch')?.eq('entity_id', batch?.id)?.order('created_at', { ascending: false }),
          supabase?.from('sales')?.select('id, sale_price_eur_per_ton, sale_price_per_ton, buyer_name, sold_at, created_at')?.eq('batch_id', batch?.id)?.maybeSingle(),
        ]);
        const events = buildTimelineFromBatch(
          batch,
          auditRes?.data || [],
          saleRes?.data || null,
        );
        setTimelineEvents(events);
      } catch (_) {}
      setLoadingHistory(false);
    };
    load();
  }, [activeTab, batch?.id]);

  if (!batch) return null;
  const marginDiff = batch?.actualMargin !== null ? batch?.actualMargin - batch?.expectedMargin : null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Detalle del lote">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: '"Outfit", sans-serif' }}>
              Lote {batch?.batchId}
            </h2>
            <p className="text-sm text-slate-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              {batch?.metal} — {batch?.category}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <BatchStatusBadge status={batch?.status} />
            <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
              <Icon name="X" size={18} color="currentColor" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 px-5">
          {TABS?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab?.id
                  ? 'border-orange-500 text-orange-600' :'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="p-5">
          {activeTab === 'details' && (
            <div className="space-y-5">
              {/* Progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Progreso de Procesamiento</span>
                  <span className="text-sm font-mono font-bold text-slate-800">{batch?.progress}%</span>
                </div>
                <ProcessingProgressBar percentage={batch?.progress} status={batch?.status} />
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'Fecha Compra', value: batch?.purchaseDate },
                  { label: 'Peso (kg)', value: batch?.weight?.toLocaleString('es-ES') },
                  { label: 'Precio Compra', value: `${batch?.purchasePrice?.toLocaleString('es-ES', { minimumFractionDigits: 2 })} €/t` },
                  { label: 'Yield Estimado', value: `${batch?.yieldEstimated}%` },
                  { label: 'Yield Real', value: batch?.yieldActual ? `${batch?.yieldActual}%` : '—' },
                  { label: 'Días Restantes', value: batch?.daysRemaining !== null ? `${batch?.daysRemaining} días` : '—' },
                  { label: 'Margen Esperado', value: `${batch?.expectedMargin?.toFixed(1)}%` },
                  { label: 'Margen Real', value: marginDiff !== null ? `${(batch?.expectedMargin + marginDiff)?.toFixed(1)}%` : '—' },
                  { label: 'Coste Logística', value: `${batch?.logisticsCost?.toLocaleString('es-ES')} €` },
                ]?.map(item => (
                  <div key={item?.label} className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{item?.label}</p>
                    <p className="text-sm font-semibold text-slate-800 font-mono">{item?.value}</p>
                  </div>
                ))}
              </div>

              {/* Notes */}
              {batch?.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-amber-700 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Notas</p>
                  <p className="text-sm text-amber-800" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{batch?.notes}</p>
                </div>
              )}

              {/* Timeline (legacy) */}
              {batch?.timeline?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3" style={{ fontFamily: '"Outfit", sans-serif' }}>Historial de Estado</h3>
                  <div className="space-y-2">
                    {batch?.timeline?.map((event, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${idx === 0 ? 'bg-orange-500' : 'bg-slate-300'}`} />
                        <div>
                          <p className="text-xs font-medium text-slate-700" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{event?.status}</p>
                          <p className="text-xs text-slate-400" style={{ fontFamily: '"JetBrains Mono", monospace' }}>{event?.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              {loadingHistory ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-slate-400" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Cargando historial...</p>
                </div>
              ) : timelineEvents?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-slate-400">
                  <Icon name="Clock" size={32} color="currentColor" strokeWidth={1.5} />
                  <p className="text-sm" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Sin eventos registrados</p>
                </div>
              ) : (
                <div className="pt-2">
                  {timelineEvents?.map((event, idx) => (
                    <TimelineItem
                      key={`${event?.type}-${event?.ts}-${idx}`}
                      event={event}
                      isLast={idx === timelineEvents?.length - 1}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-200 sticky bottom-0 bg-white rounded-b-2xl">
          <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" onClick={() => onStatusChange(batch)}>
            Cambiar Estado
          </Button>
          <Button variant="default" size="sm" iconName="Pencil" iconPosition="left" onClick={() => onEdit(batch)}>
            Editar Lote
          </Button>
        </div>
      </div>
    </div>
  );
}